import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Share2, Loader2, Download } from "lucide-react";
import axios from "axios";
import { saveAs } from "file-saver";

const SharePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const videoUrl = searchParams.get("url") || "";
  const clipName = searchParams.get("name") || "clip";
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [autoShareAttempted, setAutoShareAttempted] = useState(false);

  const fetchAndShare = async () => {
    if (!videoUrl) return;
    setIsSharing(true);
    try {
      const response = await axios.get(videoUrl, { responseType: "blob" });

      const urlPath = new URL(videoUrl, window.location.href).pathname;
      const ext = (urlPath.match(/\.([a-zA-Z0-9]+)$/)?.[1] || "mp4").toLowerCase();
      const safeName = (clipName || "clip").replace(/[^a-zA-Z0-9-_.]+/g, "_");
      const fileName = `${safeName}.${ext}`;

      const file = new File([response.data], fileName, {
        type: response.data.type || "video/mp4",
      });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: clipName });
      } else {
        saveAs(response.data, fileName);
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Share failed:", error);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleDownload = async () => {
    if (!videoUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      const urlPath = new URL(videoUrl, window.location.href).pathname;
      const ext = (urlPath.match(/\.([a-zA-Z0-9]+)$/)?.[1] || "mp4").toLowerCase();
      const safeName = (clipName || "clip").replace(/[^a-zA-Z0-9-_.]+/g, "_");
      const fileName = `${safeName}.${ext}`;

      const response = await axios.get(videoUrl, { responseType: "blob" });
      saveAs(response.data, fileName);
    } catch {
      window.open(videoUrl, "_blank", "noopener,noreferrer");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (videoUrl && !autoShareAttempted) {
      setAutoShareAttempted(true);
      fetchAndShare();
    }
  }, [videoUrl]);

  if (!videoUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <p className="text-muted-foreground">{t("sharePage.noVideo")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 gap-6">
      <div className="w-full max-w-md">
        <video
          src={videoUrl}
          crossOrigin="anonymous"
          controls
          playsInline
          className="w-full rounded-lg bg-black"
        />
      </div>

      <h2 className="text-lg font-semibold text-center truncate max-w-md w-full">
        {clipName}
      </h2>

      <div className="flex gap-3">
        <Button
          onClick={fetchAndShare}
          disabled={isSharing}
          variant="gradient"
          size="lg"
        >
          {isSharing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Share2 className="w-5 h-5" />
          )}
          {t("sharePage.share")}
        </Button>

        <Button
          onClick={handleDownload}
          disabled={isDownloading}
          variant="outline"
          size="lg"
        >
          {isDownloading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Download className="w-5 h-5" />
          )}
          {t("sharePage.download")}
        </Button>
      </div>
    </div>
  );
};

export default SharePage;
